import { Chatbot, Data, Modal, UI, Utils } from 'common';
import InlineMessage from 'common/src/code/ui/panel/InlineMessage.component';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React, { Component } from 'react';
import ManageSubscriptionPlan from '../subscriptionPlan/ManageSubscriptionPlan.component';
import ReactivatePlanModal from './../subscriptionPlan/ReactivatePlanModal.component';
import ReactivationSuccessfulModal from '../subscriptionPlan/ReactivationSuccessfulModal.component';
import AccountCommunication from '../utils/AccountCommunication';
import { RoutePath } from '../utils/RoutePath';
import AccountUtil from '../utils/Util';
import GBBPlanChangedSuccessfulModal from './GBBPlanChangedSuccessfulModal.component';
import { GBBPlanTile } from './GBBPlanTile.component';
import {
	PendingPlanClosingDisclaimer,
	PendingPlanClosingFineprint,
} from './PendingPlanClosingDisclaimer.component';
import PlanChangedModal from './PlanChangedModal.component';
import PlanDetailsModal from './PlanDetailsModal.component';
import { PlanTile } from './PlanTile.component';

const { ChatbotTriggers, launchChatbot } = Chatbot;
const { ModalContext } = Modal;

const {
	Section,
	SectionBody,
	Spinner,
	WrappedButton,
	WrappedButtonType,
	Footer,
	InfoBanner,
} = UI;
const {
	Logger,
	Session,
	withLayoutAndModalContexts,
	Analytics,
	BillingPlans,
	Communication,
	Util,
} = Utils;
const { AccountStatus, PlanIds } = Data;
const { prettyPrintPlan } = BillingPlans;

// Query params consumed downstream by CancelAccount (see retentionData there).
const RETENTION_PARAM_KEYS = [
	'decision',
	'offer',
	'reason',
	'reason_other',
	'competition',
];
const RETENTION_DECISIONS = ['cancel', 'talk', 'downgrade', 'stay'];
const RETENTION_OFFERS = [
	'basic',
	'paygo',
	'paygo2',
	'retentionbasic',
	'retentionlite',
];

// subtract base SKU (1020000) from the plan's SKU to obtain the plan ID
const getPlanId = planSku => planSku - 1020000;

// Pending changes to these plans are retention offers, not regular
// selections — surfacing a tile for them would expose an offer the customer
// isn't meant to see outside the retention flow that granted it.
const HIDDEN_PENDING_PLAN_IDS = [
	PlanIds.PAYG,
	PlanIds.RetentionBasic407,
	PlanIds.RetentionLite408,
];

class ChangePlan extends Component {
	constructor(props) {
		super(props);

		this.state = {
			plans: [],
			closeAccountEligible: false,
			pendingPlanId: null,
			isLoading: false,
			loadingMessage: '',
			selectedPlan: 0,
			currentPlanSku: null,
			isGBBUser: false,
			showGBBPageForLegacyPlans: false,
		};
	}

	async componentDidMount() {
		const {
			modalContext: { showError, showModal },
			history,
		} = this.props;
		const { accountStatus, planDetails = {} } = Session.getAccountInfo();
		const pendingPlanId = planDetails.pendingPlanId
			? getPlanId(planDetails.pendingPlanId)
			: null;

		const { planId } = planDetails;
		const isGBBUser =
			BillingPlans.isNewLineupPlan() ||
			[
				PlanIds.Core,
				PlanIds.Office,
				PlanIds.Essential,
				PlanIds.Ecommerce,
				PlanIds.CoreV380,
				PlanIds.OfficeV381,
				PlanIds.EcommerceV383,
			].indexOf(planId) !== -1;

		// WEBAPPS-26179, accounts with `PendingClosure` status shouldn't see options to change plans or cancel account
		if (accountStatus === AccountStatus.PendingClosure) {
			return;
		}

		this.displayLoadingSpinner(true, 'Loading plans...');

		const billingResponse = await AccountCommunication.billingPlans();
		const eligibleResponse = await AccountCommunication.closeAccountEligible();

		this.displayLoadingSpinner(false);

		// handle billingPlans response
		if (AccountCommunication.isSuccessful(billingResponse)) {
			let sortedPlans;
			const getCurrentPlan = billingResponse.data.BillingPlans.find(
				plan => plan.Sku === billingResponse.data.CurrentPlanSku
			);
			const showGBBPageForLegacyPlanUsers = getCurrentPlan.Price <= 20.99;

			if (!isGBBUser && !showGBBPageForLegacyPlanUsers) {
				sortedPlans = billingResponse.data.BillingPlans.sort(
					(a, b) => b.Price - a.Price
				);
				const currentPlan = sortedPlans
					.map(plan => plan.Sku)
					.indexOf(billingResponse.data.CurrentPlanSku);

				sortedPlans.unshift(...sortedPlans.splice(currentPlan, 1)); // move current plan to beginning of the array
			} else {
				sortedPlans = billingResponse.data.BillingPlans.sort(
					(a, b) => a.Price - b.Price
				);
			}

			this.setState({
				plans: sortedPlans,
				pendingPlanId,
				currentPlanSku: billingResponse.data.CurrentPlanSku,
				isGBBUser,
				showGBBPageForLegacyPlans: getCurrentPlan.Price <= 20.99,
			});
		} else {
			// return out of function if this call errors out to prevent multiple error modals
			return showError({
				response: billingResponse,
			});
		}

		// handle closeAccountEligible response
		if (AccountCommunication.isSuccessful(eligibleResponse)) {
			this.setState({
				closeAccountEligible: eligibleResponse.data.CloseAccountEligible,
			});
		} else {
			showError({
				response: eligibleResponse,
			});
		}

		// Collect retention data from URL. Only the known keys read by the cancel
		// flow are kept; decision/offer are validated against their known values.
		const params = new URLSearchParams(window.location.search);
		const retention = {};
		RETENTION_PARAM_KEYS.forEach(key => {
			if (params.has(key)) {
				retention[key] = params.get(key);
			}
		});
		if (!RETENTION_DECISIONS.includes(retention.decision)) {
			delete retention.decision;
		}
		if (!RETENTION_OFFERS.includes(retention.offer)) {
			delete retention.offer;
		}
		if (Object.keys(retention).length > 0) {
			localStorage.setItem('retentionData', JSON.stringify(retention));

			if (retention.decision === 'cancel') {
				localStorage.setItem('hasCancelledViaBrightback', 'true');
				history.push(RoutePath.cancelAccount);
			} else if (retention.decision === 'talk') {
				launchChatbot({
					flags: this.props.flags,
					showModal,
					trigger: ChatbotTriggers.CancelAccount,
				});
				history.push(RoutePath.changePlan);
			} else if (
				retention.decision === 'downgrade' &&
				retention.offer === 'basic'
			) {
				localStorage.setItem('hasAcceptedBasicOffer', 'true');
				// Go to cancel account page since downgrade path isnt available here.
				history.push(RoutePath.cancelAccount);
			} else if (
				retention.decision === 'downgrade' &&
				retention.offer === 'paygo'
			) {
				localStorage.setItem('hasAcceptedPaygoOffer', 'true');
				// Go to cancel account page since downgrade path isnt available here.
				history.push(RoutePath.cancelAccount);
			} else if (
				retention.decision === 'downgrade' &&
				retention.offer === 'paygo2'
			) {
				localStorage.setItem('hasAcceptedPaygoOffer2', 'true');
				// Go to cancel account page since downgrade path isnt available here.
				history.push(RoutePath.cancelAccount);
			} else if (
				retention.decision === 'downgrade' &&
				retention.offer === 'retentionbasic'
			) {
				localStorage.setItem('hasAcceptedRetentionBasicOffer', 'true');
				// Go to cancel account page since downgrade path isnt available here.
				history.push(RoutePath.cancelAccount);
			} else if (
				retention.decision === 'downgrade' &&
				retention.offer === 'retentionlite'
			) {
				localStorage.setItem('hasAcceptedRetentionLiteOffer', 'true');
				// Go to cancel account page since downgrade path isnt available here.
				history.push(RoutePath.cancelAccount);
			} else if (retention.decision === 'stay') {
				history.push(RoutePath.changePlan);
			}
		}
	}

	onSelectPlan = index => {
		const { plans } = this.state;

		this.setState({
			selectedPlan: index,
		});

		Logger.logToServer({
			category: 'Select Plan',
			Plan: plans[index].Name,
		});
	};

	onClickChangePlan = async () => {
		const { plans, selectedPlan } = this.state;
		const {
			modalContext: { showError, showModal },
		} = this.props;
		const newPlan = plans[selectedPlan];
		const planId = getPlanId(newPlan.Sku);
		const { closeRequestedDate = false } = Session.getAccountInfo();

		const data = {
			PlanId: planId,
			Disposition: {},
			CancelReason: {},
			newPlanPrice: newPlan.Price,
		};

		// If account is pending closure, reactivate plan before changing plan
		if (closeRequestedDate) {
			const reactivatePlanResponse =
				await AccountCommunication.reactivatePlan();

			Analytics.trackEvent(
				'reactivate_plan',
				{
					type: 'change',
					selectedPlanId: planId,
					selectedPlanName: newPlan && newPlan.Name ? newPlan.Name : '',
				},
				Analytics.agent.segment
			);

			if (!AccountCommunication.isSuccessful(reactivatePlanResponse)) {
				showError({ response: reactivatePlanResponse });
				return;
			}
		}

		Logger.saveActionData('Change Plan Button Clicked', { Plan: newPlan.Name });

		this.displayLoadingSpinner(true, 'Changing plan...');

		const response = await AccountCommunication.changePlan(data);

		this.displayLoadingSpinner(false);

		if (AccountCommunication.isSuccessful(response)) {
			this.setState({
				pendingPlanId: planId,
			});
			showModal({
				component: PlanChangedModal,
				data: {
					newPlanName: newPlan.Name,
					newPlanCost: newPlan.Price,
				},
			});
		} else {
			showError({ response });
		}

		Logger.logToServer({
			category: 'Change Plan',
			Plan: newPlan.Name,
		});
	};

	onGBBChangePlan = async (currentPlanSku, oldPlanSku, isUpgrade) => {
		const { plans } = this.state;
		const {
			modalContext: { showError, showModal },
		} = this.props;
		const newPlanId = getPlanId(currentPlanSku);
		const newPlan = plans.find(plan => plan.Sku === currentPlanSku);
		const oldPlan = plans.find(plan => plan.Sku === oldPlanSku);

		const planChangedProperties = {
			prior_plan_selected: oldPlan.Name,
			prior_monthly_plan_cost: oldPlan.Price,
			new_plan_selected: newPlan.Name,
			new_monthly_plan_cost: newPlan.Price,
		};

		const data = {
			PlanId: newPlanId,
			Disposition: {},
			CancelReason: {},
			newPlanPrice: newPlan.Price,
		};

		const { closeRequestedDate = false } = Session.getAccountInfo();

		this.displayLoadingSpinner(true, 'Changing plan...');

		// If account is pending closure, reactivate plan before changing plan
		if (closeRequestedDate) {
			const reactivatePlanResponse =
				await AccountCommunication.reactivatePlan();

			Analytics.trackEvent(
				'reactivate_plan',
				{
					type: 'change',
					selectedPlanId: newPlanId,
					selectedPlanName: newPlan && newPlan.Name ? newPlan.Name : '',
				},
				Analytics.agent.segment
			);

			if (!AccountCommunication.isSuccessful(reactivatePlanResponse)) {
				showError({ response: reactivatePlanResponse });
				return;
			}
		}

		const response = await AccountCommunication.changePlan(data);

		this.displayLoadingSpinner(false);

		if (AccountCommunication.isSuccessful(response)) {
			this.setState({
				pendingPlanId: newPlanId,
			});

			setTimeout(async () => {
				await Session.loadAccountInfo();
			}, 7000);

			showModal({
				component: GBBPlanChangedSuccessfulModal,
				data: {
					newPlanCost: newPlan.Price,
					previousPlanName: oldPlan.Name,
					isUpgrade,
				},
			});
		} else {
			showError({ response });
		}

		Analytics.trackEvent(
			'planSelectionChanged',
			planChangedProperties,
			Analytics.agent.segment
		);
	};

	onClickCloseAccount = async () => {
		const { history } = this.props;

		const accountDetails = Session.getAccountInfo();

		const { useBrightback = false } = this.props.flags || {};

		if (useBrightback) {
			const result = await AccountUtil.sendUserToRetention(accountDetails);

			if (result.startsWith('Error')) {
				Logger.logToServer({
					category: 'error_brightback_flow',
					message: result,
				});
				history.push(RoutePath.cancelAccount);
			} else {
				window.location.href = result;
			}
		} else {
			history.push(RoutePath.cancelAccount);
		}

		Logger.logToServer({
			category: 'Close Account',
		});
	};

	displayLoadingSpinner = (isLoading, loadingMessage = '') => {
		this.setState({
			isLoading,
			loadingMessage,
		});
	};

	parseDescription = splitText =>
		splitText.map((text, i) => (
			<div key={i} className="list-item">
				<div className="icon" />
				<p key={i}>{text}</p>
			</div>
		));

	renderMappedTiles = pendingPlanId => {
		const {
			plans,
			currentPlanSku,
			isGBBUser,
			selectedPlan,
			showGBBPageForLegacyPlans,
		} = this.state;
		const {
			layoutContext: { isMobile },
			history,
		} = this.props;
		const mobileCls = isMobile ? ' mobile-wrapper' : '';
		const { closeRequestedDate, nextBillingDate } = Session.getAccountInfo();

		if (isGBBUser || showGBBPageForLegacyPlans) {
			const currentPlan = plans.find(plan => plan.Sku === currentPlanSku);
			const currentPlanPrice = currentPlan.Price;

			return plans.map((plan, i) => {
				const { Name, Price, PricePeriod, Sku, Summary } = plan;
				const isCurrentPlan = currentPlanSku === Sku;
				const planId = getPlanId(Sku);
				const isPlanInProgress = pendingPlanId === planId;

				if (isCurrentPlan) {
					return null;
				}

				if (isPlanInProgress && HIDDEN_PENDING_PLAN_IDS.includes(planId)) {
					return null;
				}

				return (
					<GBBPlanTile
						key={i + Name}
						planName={Name}
						price={Price}
						pricePeriod={PricePeriod}
						summary={Summary}
						mobileCls={mobileCls}
						isCurrentPlan={isCurrentPlan}
						currentPlanSku={currentPlanSku}
						currentPlanPrice={currentPlanPrice}
						currentPlanName={currentPlan.Name}
						sku={Sku}
						pendingPlanId={pendingPlanId}
						isPlanInProgress={isPlanInProgress}
						onGBBChangePlan={this.onGBBChangePlan}
						parseDescription={this.parseDescription}
					/>
				);
			});
		} else {
			return plans.map((plan, i) => {
				const { Name, Description, Price, PricePeriod, Sku, Summary } = plan;
				const isCurrentPlan = i === 0;
				const planId = getPlanId(Sku);
				const isInProgress = pendingPlanId === planId;

				if (isInProgress && HIDDEN_PENDING_PLAN_IDS.includes(planId)) {
					return null;
				}

				return (
					<PlanTile
						key={i + Name}
						index={i}
						planName={Name}
						description={Description}
						price={Price}
						pricePeriod={PricePeriod}
						summary={Summary}
						mobileCls={mobileCls}
						isCurrentPlan={isCurrentPlan}
						isInProgress={isInProgress}
						onSelectPlan={this.onSelectPlan}
						selectedPlan={selectedPlan === i}
						closeRequestedDate={closeRequestedDate}
						nextBillingDate={nextBillingDate}
						history={history}
					/>
				);
			});
		}
	};

	renderClosedAccountMessage = () => {
		const contactNumber = Util.getFraudNumber();

		return (
			<SectionBody>
				<p>
					Your Stamps.com account has been closed. If you have any questions
					about your account status, please contact Customer Support by calling{' '}
					{contactNumber} Monday-Friday 8 am - 8 pm Central Time.
				</p>
			</SectionBody>
		);
	};

	renderPlans = () => {
		const {
			closeAccountEligible,
			plans,
			pendingPlanId,
			isGBBUser,
			selectedPlan,
			showGBBPageForLegacyPlans,
		} = this.state;
		const { closeRequestedDate } = Session.getAccountInfo();

		if (isGBBUser || showGBBPageForLegacyPlans) {
			return (
				<>
					<SectionBody>
						{/* Legacy Account. GBB will be shown a ManageSubscriptionPlan component */}
						{closeRequestedDate && (
							<PendingPlanClosingDisclaimer
								accountInfo={Session.getAccountInfo()}
							/>
						)}

						{pendingPlanId === 382 && (
							<div className="info-banner-container">
								<InfoBanner type="inform">
									<span className="icon-inform" />
									<p className="info-content">
										<b>Subscription-Free plan pending:&nbsp;</b>
										Your account will update to the Subscription-Free plan once
										this current billing cycle ends.
									</p>
								</InfoBanner>
							</div>
						)}
						{this.renderLegacyInformationalTile(pendingPlanId)}
						<div
							className="subscription-info-text"
							data-qa="subscription-info-text"
							{...{ dataqa: 'subscription-info-text' }}
						>
							<p>
								Mailing and shipping from multiple locations? Contact us about
								our{' '}
								<a
									href="https://www.stamps.com/enterprise/"
									target="_blank"
									rel="noreferrer"
								>
									Multi-Location Plan.
								</a>
							</p>
						</div>
						{!closeRequestedDate && (
							<div className="cancel-subscription">
								<p className="title">Close Your Account</p>
								<p>
									When you close your account, you will lose all of your account
									history.
								</p>
								<br />
								<WrappedButton
									label="Close Account"
									className="gbb-button cancel-account"
									onClick={this.onClickCloseAccount}
									dataqa="cancel-subscription"
									buttonType="secondary"
								/>
							</div>
						)}
					</SectionBody>
					<Footer />
				</>
			);
		} else {
			const selectedPlanId = plans[selectedPlan]
				? getPlanId(plans[selectedPlan].Sku)
				: null;
			const disableChangePlan =
				selectedPlan === 0 || selectedPlanId === pendingPlanId;
			const contactNumber = Util.getFraudNumber();

			console.log('ChangePlan Non Legacy');

			return (
				<>
					<SectionBody>
						{closeRequestedDate && (
							<PendingPlanClosingDisclaimer
								accountInfo={Session.getAccountInfo()}
							/>
						)}
						{this.renderMappedTiles(pendingPlanId)}
						<div
							className="tax-exemption"
							data-qa="tax-exemption"
							{...{ dataqa: 'tax-exemption' }}
						>
							*Plus applicable taxes, if any. Tax exempt? Please send your
							tax-exempt documentation along with your username to{' '}
							<a href="mailto:taxexempt@stamps.com">taxexempt@stamps.com</a>.
						</div>
						{/* WEBAPPS-26345, show cancellation text if user is ineligible for online cancellation  */}
						{!closeAccountEligible && (
							<div
								className="tax-exemption"
								data-qa="cancellation-text"
								{...{ dataqa: 'cancellation-text' }}
							>
								In addition, if you have any questions regarding your plan,
								including making modifications or canceling your plan, please
								call us at {contactNumber}.
							</div>
						)}
						<WrappedButton
							label="Change Plan"
							className="change-plan-button"
							onClick={this.onClickChangePlan}
							disabled={disableChangePlan}
							dataqa="change-plan-button"
							buttonType="primary"
						/>
						{!closeRequestedDate && (
							<div className="cancel-subscription">
								<p className="title">Cancel Subscription</p>
								<p>
									Please note that you will permanently lose your account
									history if you{' '}
									<button
										type="button"
										onClick={this.onClickCloseAccount}
										data-qa="cancel-subscription"
										{...{ dataqa: 'cancel-subscription' }}
										style={{
											background: 'none',
											border: 'none',
											padding: 0,
											color: 'inherit',
											textDecoration: 'underline',
											cursor: 'pointer',
											font: 'inherit',
										}}
									>
										close your account.
									</button>
								</p>
							</div>
						)}
					</SectionBody>
				</>
			);
		}
	};

	onReactivatePlan = () => {
		const {
			modalContext: { showModal },
			history,
		} = this.props;

		const reactivatePlan = async () => {
			Analytics.trackEvent(
				'reactivate_plan',
				{ type: 'confirm' },
				Analytics.agent.segment
			);

			AccountCommunication.reactivatePlan().then(async response => {
				if (Communication.isSuccessful(response)) {
					await Session.loadAccountInfo();
					const _reactivationModalId = showModal({
						component: ReactivationSuccessfulModal,
						data: {
							onConfirm: () => {
								history.push(RoutePath.changePlan);
							},
						},
					});
				} else {
					Communication.showError(response.message);
				}
			});
		};

		Analytics.trackEvent(
			'reactivate_plan',
			{ type: 'click' },
			Analytics.agent.segment
		);

		showModal({
			component: ReactivatePlanModal,
			data: {
				onConfirm: reactivatePlan,
			},
		});
	};

	// if showGBBPageForLegacyPlans is true, show all the GBB plans with informational banner tile of legacy plan at the top
	renderLegacyInformationalTile = pendingPlanId => {
		const {
			layoutContext: { isMobile },
		} = this.props;
		const { plans, currentPlanSku, showGBBPageForLegacyPlans } = this.state;
		const mobileCls = isMobile ? ' mobile-wrapper' : '';
		const currentPlan = plans.find(plan => plan.Sku === currentPlanSku);
		const isPaygoPlan = currentPlanSku === 1020382;
		const splitText = currentPlan.Summary.split('  -').filter(
			text => text !== ''
		);
		const planTag = splitText.shift();
		const planSubHeader = splitText.shift();
		const planClassName = isPaygoPlan ? 'plan-info' : 'legacy-plan-info';
		const { nextBillingDate, closeRequestedDate } = Session.getAccountInfo();

		return (
			<>
				<div className={`gbb-account-plan-tile selected${mobileCls}`}>
					{pendingPlanId === null && (
						<p className="current-plan-title">Current Plan</p>
					)}
					{closeRequestedDate && (
						<>
							<PendingPlanClosingFineprint nextBillingDate={nextBillingDate} />
						</>
					)}
					{showGBBPageForLegacyPlans && (
						<div className={planClassName}>
							<div className="plan-details">
								<div>
									<p className="plan-name">
										{prettyPrintPlan(currentPlan.Name)}
									</p>
									<p className="plan-name-tag">{planTag}</p>
								</div>
								<div className="plan-cost">
									${currentPlan.Price}
									<span className="plan-mo">/mo</span>
								</div>
							</div>
							{!isPaygoPlan && (
								<>
									<div className="info-banner-container">
										<InlineMessage
											type="warning"
											dataqa="legacy-plan-disclaimer"
											header="As of May 2024, your plan will not receive feature updates."
											text="You're welcome to continue with your current plan. The price and features will not change. To access new features, consider upgrading to one of the plans below."
										/>
									</div>
									<button
										type="button"
										className="details"
										onClick={this.launchDetailsModal}
									>
										View current plan details
									</button>
								</>
							)}
							{isPaygoPlan && (
								<div className="plan-content">
									<p className="plan-sub-header">{planSubHeader}</p>
									<div className="plan-list">
										<div>{this.parseDescription(splitText)}</div>
									</div>
								</div>
							)}
							{pendingPlanId === null && closeRequestedDate && (
								<div className="reactivate-plan-button">
									<WrappedButton
										label="Reactivate Plan"
										buttonType={WrappedButtonType.Primary}
										onClick={this.onReactivatePlan}
										dataqa="reactivate-plan"
									/>
								</div>
							)}
						</div>
					)}
				</div>
				{this.renderMappedTiles(pendingPlanId)}
			</>
		);
	};

	launchDetailsModal = () => {
		const {
			modalContext: { showModal },
		} = this.props;
		const { plans, currentPlanSku } = this.state;
		const currentPlan = plans.find(plan => plan.Sku === currentPlanSku);

		showModal({
			component: PlanDetailsModal,
			data: {
				title: currentPlan.Name,
				description: currentPlan.Description,
			},
		});
	};

	render() {
		const { isLoading, loadingMessage, isGBBUser, plans } = this.state;
		const { accountStatus } = Session.getAccountInfo();
		const { history } = this.props;

		// Don't render legacy UI while plans are still loading —
		// isGBBUser is only known after componentDidMount fetches billing data.
		if (plans.length === 0 && accountStatus !== AccountStatus.PendingClosure) {
			return <Spinner msg="Loading plans..." />;
		}

		if (isGBBUser) {
			return (
				<ManageSubscriptionPlan
					flags={this.props.flags}
					onNavigate={(path, state) => history.push(path, state)}
					locationState={this.props.location?.state}
				/>
			);
		} else {
			return (
				<div className="account-change-plan">
					<Section>
						<div className="account-title">Subscription Plan</div>
						{/* WEBAPPS-26179, accounts with `PendingClosure` status shouldn't see options to change plans or cancel account */}
						{accountStatus === AccountStatus.PendingClosure
							? this.renderClosedAccountMessage()
							: this.renderPlans()}
					</Section>
					{isLoading && <Spinner msg={loadingMessage} />}
				</div>
			);
		}
	}
}

export default withLDConsumer()(withLayoutAndModalContexts(ChangePlan));
